import { logger } from "../logger";
import type {
  SimpleTableConfig,
  TableFilters,
  SerializedViewState,
} from "@/components/shared/UniversalCRMTable/types";
import type {
  FieldFilterContext,
  SimpleFieldConfig,
} from "@/lib/types/table/field-config-types";
import { buildGraphQLFilters } from "@/lib/utils/formatting/datetime-filter-translator";
import {
  GRAPHQL_OPERATOR_KEYS,
  GRAPHQL_LOGICAL_KEYS,
  CUSTOM_FILTER_FIELD_ALIASES,
  resolveGraphQLFieldName,
  convertArrayValueToGraphQL,
  buildRangeFilter,
} from "./filter-conversion-helpers";

export function normalizeSerializedFilters(filters: Record<string, any>): TableFilters {
  const normalized: TableFilters = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;

    if (Array.isArray(value)) {
      normalized[key] = value as string[];
    } else if (typeof value === "string") {
      if (value.includes(",")) {
        normalized[key] = value.split(",").map((v) => v.trim()).filter((v) => v !== "");
      } else {
        normalized[key] = value;
      }
    } else if (typeof value === "number" || typeof value === "boolean") {
      normalized[key] = value;
    } else if (
      typeof value === "object" && value !== null && !Array.isArray(value) &&
      ("min" in value || "max" in value)
    ) {
      normalized[key] = value;
    }
  });

  return normalized;
}

/**
 * Virtual filters live in the filter object under a stable id but don't
 * correspond to a real field declaration (so they have no `toGraphQLFilter`
 * hook). They're handled by this small dispatcher map keyed by the filter id
 * — keeping them out of `entityName === "..."` branches inside the iterator.
 *
 * Add new virtual filter ids here; the corresponding saved-view filter object
 * should set the key to a truthy value (typically `true`).
 */
type VirtualFilterHandler = (
  value: unknown,
  context: FieldFilterContext,
) => Record<string, unknown> | null;

const VIRTUAL_FILTER_HANDLERS: Record<string, VirtualFilterHandler> = {
  /**
   * Deals — "not followed up this week" virtual filter. Routed through the
   * `filter_deals_by_follow_up_status` RPC. Backed by the
   * `useDealsNotFollowedUpThisWeek` saved-view filter hook (see
   * `features/deals/config/deal-saved-views.ts`).
   */
  not_followed_up_this_week: (value) => {
    if (value !== true) return null;
    return {
      _rpcFilter: {
        type: "filter_deals_by_follow_up_status",
        params: { p_followed_up: false, p_limit: 1000, p_offset: 0 },
      },
    };
  },
  /**
   * Deals — "scheduled follow-ups" virtual filter. Lists active deals with a
   * scheduled follow-up tag for the current or a future week. Routed through the
   * `filter_deals_with_scheduled_followup` RPC. Backed by the `scheduled-followups`
   * saved view (see `features/deals/config/deal-saved-views.ts`).
   */
  scheduled_followup: (value) => {
    if (value !== true) return null;
    return {
      _rpcFilter: {
        type: "filter_deals_with_scheduled_followup",
        params: { p_limit: 1000, p_offset: 0 },
      },
    };
  },
};

function resolveFieldConfig(
  config: SimpleTableConfig<any>,
  rawKey: string,
  resolvedFieldName: string,
): SimpleFieldConfig | undefined {
  const fields = config.fields as Record<string, SimpleFieldConfig> | undefined;
  if (!fields) return undefined;
  return fields[rawKey] ?? fields[resolvedFieldName];
}

export function convertToGraphQLFilter(
  filters: Record<string, any>,
  config: SimpleTableConfig<any>,
  entityName: string,
): Record<string, any> {
  const graphqlFilter: Record<string, any> = {};
  const context: FieldFilterContext = { now: new Date() };

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;

    const graphqlFieldName = resolveGraphQLFieldName(key, entityName);

    // 1. Virtual filter ids (no real field declaration)
    const virtualHandler = VIRTUAL_FILTER_HANDLERS[graphqlFieldName];
    if (virtualHandler) {
      const virtualResult = virtualHandler(value, context);
      if (virtualResult) {
        Object.assign(graphqlFilter, virtualResult);
        return;
      }
    }

    // 2. Field-declared `toGraphQLFilter` hook
    const fieldConfig = resolveFieldConfig(config, key, graphqlFieldName);
    if (fieldConfig?.toGraphQLFilter) {
      const hookResult = fieldConfig.toGraphQLFilter(value, context);
      if (hookResult) {
        Object.assign(graphqlFilter, hookResult);
        return;
      }
    }

    // 3. Generic translators (array / range / datetime presets / equals)
    if (Array.isArray(value)) {
      // An EMPTY multi-select is "no constraint", not "match nothing".
      //
      // The skip list at the top of this loop drops null / undefined / "" but
      // never `[]`, so clearing a tags/assignees picker used to emit
      // `{ tags: { overlaps: [] } }` (or `{ field: { in: [] } }`) — a predicate
      // no row can satisfy. The user cleared a filter and the table went EMPTY
      // instead of showing everything, which reads as "there is no data".
      //
      // Skipped HERE rather than beside the null check on purpose: a
      // field-declared `toGraphQLFilter` hook or a virtual-filter handler runs
      // first and may legitimately give `[]` its own meaning, and this must not
      // reach past them. "Has no tags" is not expressible as `[]` in any case —
      // it is a hook returning an `is: NULL` style predicate (see the
      // `last_contacted_at: "never"` field hook), which this leaves untouched.
      //
      // NOTE: `normalizeCustomFiltersToGraphQL` below deliberately does NOT do
      // this. Its input is programmatic (a computed id list), where an empty
      // array is a real constraint — dropping it there would WIDEN the query.
      if (value.length === 0) return;
      Object.assign(graphqlFilter, convertArrayValueToGraphQL(value, graphqlFieldName, entityName, config));
    } else if (
      typeof value === "object" && value !== null && !Array.isArray(value) &&
      ("min" in value || "max" in value)
    ) {
      const range = buildRangeFilter(value as { min?: number; max?: number });
      if (range) graphqlFilter[graphqlFieldName] = range;
    } else {
      if (
        fieldConfig &&
        (fieldConfig.type === "datetime" || fieldConfig.type === "date") &&
        fieldConfig.datetimePresets?.enabled &&
        typeof value === "string"
      ) {
        Object.assign(graphqlFilter, buildGraphQLFilters({ [graphqlFieldName]: value }));
      } else {
        graphqlFilter[graphqlFieldName] = { eq: value };
      }
    }
  });

  return graphqlFilter;
}

export function convertSavedViewToGraphQLFilter(
  serializedState: SerializedViewState,
  config: SimpleTableConfig<any>,
  entityName: string
): Record<string, any> {
  const filterFields: Record<string, any> = serializedState.filters || {};
  const normalizedFilters = normalizeSerializedFilters(filterFields);
  return convertToGraphQLFilter(normalizedFilters, config, entityName);
}

export function convertFilterContextToGraphQL(
  simpleFilters: Record<string, any>,
  searchTerm: string,
  config: SimpleTableConfig<any>,
  entityName: string,
  _customFilterHooks?: Record<string, () => Record<string, any>>
): Record<string, any> {
  let graphqlFilter: Record<string, any> = {};

  if (Object.keys(simpleFilters).length > 0) {
    const normalized = normalizeSerializedFilters(simpleFilters);
    graphqlFilter = convertToGraphQLFilter(normalized, config, entityName);
  }

  if (searchTerm) {
    graphqlFilter._search = searchTerm;
  }

  return graphqlFilter;
}

export function normalizeCustomFiltersToGraphQL(
  customFilters: Record<string, any>
): Record<string, any> {
  if (!customFilters || Object.keys(customFilters).length === 0) return {};

  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(customFilters)) {
    if (value === null || value === undefined) continue;

    if (GRAPHQL_LOGICAL_KEYS.has(key)) {
      out[key] = value;
      continue;
    }

    const graphqlKey = CUSTOM_FILTER_FIELD_ALIASES[key] ?? key;

    if (
      typeof value === "object" && !Array.isArray(value) && value !== null &&
      Object.keys(value).some((k) => GRAPHQL_OPERATOR_KEYS.has(k))
    ) {
      out[graphqlKey] = value;
    } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out[graphqlKey] = { eq: value };
    } else if (Array.isArray(value)) {
      out[graphqlKey] = { in: value };
    } else {
      out[graphqlKey] = { eq: value };
    }
  }
  return out;
}

export function buildEntityRelationshipFilter(
  relationship: Record<string, string | number | boolean | null | undefined>
): Record<string, any> {
  const cleaned: Record<string, any> = {};
  for (const [k, v] of Object.entries(relationship)) {
    if (v != null && v !== "") cleaned[k] = v;
  }
  return normalizeCustomFiltersToGraphQL(cleaned);
}

export function debugFilterConversion(label: string, input: any, output: any): void {
  if (process.env.NODE_ENV === "development") {
    logger.debug("filter conversion", {
      op: "filter_conversion_debug",
      label,
      input,
      output,
    });
  }
}
